import { PopoverButton, Popover, PopoverPanel } from '@headlessui/react';
import useModifierKeyPressed from '../../../../../shared/hooks/useModifierKeyPressed';
import { Position } from '@xyflow/react';
import { NodeId } from '@diagram/model/node.model';
import NodeIcon from '../node.icon';
import { calculatePosition, popoverConfig } from '../node.utils';
import { ShapeConfig, shapeMap } from './shape.map';
import { ShapeTypes } from '../../../model/node.model';
import { useCallback, useMemo } from 'react';
import { useDiagramStore } from '@diagram/ui/hooks/diagram-store.hook';
interface Props {
  nodeId: NodeId;
  position: Position;
}

const calculateInversePosition = (position: Position): Position => {
  switch (position) {
    case Position.Top:
      return Position.Bottom;
    case Position.Bottom:
      return Position.Top;
    case Position.Left:
      return Position.Right;
    case Position.Right:
      return Position.Left;
  }
};

const ShapePopover = ({ position, nodeId }: Props) => {
  // const { addNode, addEdge, insertNewNodeOnEdge } = useDiagramActions();
  const createNodeAt = useDiagramStore((s) => s.createNodeAt);
  const createEdge = useDiagramStore((s) => s.edge.createEdge);
  const nodesById = useDiagramStore((s) => s.nodesById);
  const isModifierPressed = useModifierKeyPressed();
  const {
    btnClassName,
    popoverClassName,
    icon: Icon,
  } = popoverConfig[position];

  const vertical = position === 'left' || position === 'right';

  const node = useMemo(() => {
    return nodesById[nodeId];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId]);

  const handleAddShape = useCallback(
    (config: ShapeConfig, shape: ShapeTypes, close: () => void) => {
      const nodePosition = node.position;
      const nodeDimensions = {
        width: node.width || 0,
        height: node.height || 0,
      };

      const xyPosition = calculatePosition(
        position,
        nodePosition,
        { width: config.width, height: config.height },
        nodeDimensions
      );

      const newNodeId = createNodeAt({
        shape,
        type: 'shape',
        ...xyPosition,
        width: config.width,
        height: config.height,
      });

      createEdge({
        source: nodeId,
        target: newNodeId,
        sourceHandle: position,
        targetHandle: calculateInversePosition(position),
      });

      close(); // close the popover after adding the shape
    },
    [createEdge, createNodeAt, node, nodeId, position]
  );

  return (
    <Popover>
      {({ close }) => (
        <>
          <PopoverButton
            className={`${btnClassName} absolute opacity-0 ${
              !isModifierPressed && 'hover:opacity-100'
            } transition bg-white`}
          >
            <Icon className="size-5 text-gray-500 hover:text-primary" />
          </PopoverButton>
          <PopoverPanel
            className={`${popoverClassName} absolute z-50 bg-white border border-primary rounded shadow-xl p-2`}
          >
            <div
              className={`flex ${
                vertical && 'flex-col'
              } gap-2 justify-evenly items-center`}
            >
              {Object.entries(shapeMap).map(([shapeKey, config]) => (
                <button
                  key={shapeKey}
                  className={` px-2 py-1 stroke-5 stroke-gray-500 hover:stroke-accent`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddShape(config, shapeKey as ShapeTypes, close);
                  }}
                >
                  <NodeIcon
                    render={config.render}
                    shapeWidth={config.width}
                    shapeHeight={config.height}
                    height={20}
                  />
                </button>
              ))}

              {/* Add more draggable nodes here */}
            </div>
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};
export default ShapePopover;
